/**
 * Automatisches Hinzufuegen/Entfernen von Free Gifts im Theme (Cart-Drawer/Cart-Seite).
 *
 * Zwei unabhaengige Regelquellen werden hier zusammengefuehrt:
 * 1. Shop-Metafield `custom.free_gifts` (App "hensslers-discounts") - spiegelt die Logik
 *    der Checkout-UI-Extension. Siehe: hensslers-discounts/docs/theme-free-gift-integration.md
 * 2. Legacy-Settings aus `snippets/cart-gamification.liquid` (Free Gift zu jeder Bestellung,
 *    Conditional Gift per Page-Visit-Flag) - unabhaengig von custom.free_gifts, bewusst
 *    beibehalten, da die neue Config dafuer keine Entsprechung hat.
 */
(function () {
  const CONFIG_ELEMENT_ID = 'FreeGiftConfig';
  const FETCH_TIMEOUT_MS = 8000;

  let isSyncing = false;
  let rerunQueued = false;

  function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timeout));
  }

  function readConfig() {
    const el = document.getElementById(CONFIG_ELEMENT_ID);
    if (!el) return null;
    try {
      const config = JSON.parse(el.textContent);
      if (!config) return null;
      return { config, country: el.dataset.country || '' };
    } catch (error) {
      console.error('[FreeGift] Konnte Config nicht parsen', error);
      return null;
    }
  }

  function readLegacyConfig() {
    const el = document.querySelector('.gamification');
    if (!el) return null;
    return {
      everyOrderVariantId: el.getAttribute('free_gift_every_order') || null,
      conditionalVariantId: el.getAttribute('conditional-gift-id') || null,
      conditionalThreshold: Number(el.dataset.conditionalThreshold) || null,
    };
  }

  function getBlockedVariantIds() {
    return localStorage.getItem('blockgift') || '';
  }

  function selectRules(config, country) {
    if (config.enabled === false) return [];
    const byCountry = config.byCountry || {};
    const countryRules = byCountry[country];
    if (Array.isArray(countryRules) && countryRules.length) return countryRules;
    return Array.isArray(config.default) ? config.default : [];
  }

  function numericIdFromGid(value) {
    if (value == null) return null;
    const match = String(value).match(/(\d+)\s*$/);
    return match ? match[1] : null;
  }

  function computeAmount(cart) {
    const giftCardTotal = cart.items
      .filter((item) => item.gift_card)
      .reduce((sum, item) => sum + item.line_price, 0);
    return cart.total_price - giftCardTotal;
  }

  function findGiftLine(cart, variantIdNumeric) {
    return cart.items.find(
      (item) => String(item.variant_id) === variantIdNumeric && item.properties && item.properties._gift === 'true'
    );
  }

  function isScheduled(rule, now) {
    if (rule.startsAt && now < new Date(rule.startsAt)) return false;
    if (rule.endsAt && now >= new Date(rule.endsAt)) return false;
    return true;
  }

  function buildActions(cart, rules, blockedVariantIds) {
    const additions = [];
    const changes = [];
    const amount = computeAmount(cart);
    const now = new Date();

    rules.forEach((rule) => {
      const variantIdNumeric = numericIdFromGid(rule.variantId);
      if (!variantIdNumeric || !rule.threshold) return;
      if (blockedVariantIds.includes(variantIdNumeric)) return;

      const giftLine = findGiftLine(cart, variantIdNumeric);
      const qualifies = amount >= rule.threshold * 100 && isScheduled(rule, now);
      const settled = qualifies === Boolean(giftLine) && (!giftLine || giftLine.quantity === 1);
      if (settled) return;

      if (qualifies && !giftLine) {
        additions.push({
          id: parseInt(variantIdNumeric, 10),
          quantity: 1,
          properties: { _gift: 'true', _gift_title: rule.title || '' },
        });
      } else if (!qualifies && giftLine) {
        changes.push({ id: giftLine.key, quantity: 0 });
      } else if (qualifies && giftLine && giftLine.quantity > 1) {
        changes.push({ id: giftLine.key, quantity: 1 });
      }
    });

    return { additions, changes };
  }

  function collectAllConfiguredVariantIds(config) {
    const ids = new Set();
    const collect = (rules) =>
      (rules || []).forEach((rule) => {
        const id = numericIdFromGid(rule.variantId);
        if (id) ids.add(id);
      });
    collect(config.default);
    Object.values(config.byCountry || {}).forEach(collect);
    return ids;
  }

  function collectManagedOrphanGiftLines(cart, variantIds) {
    return cart.items
      .filter((item) => item.properties && item.properties._gift === 'true' && variantIds.has(String(item.variant_id)))
      .map((item) => ({ id: item.key, quantity: 0 }));
  }

  function buildLegacyActions(cart, legacyConfig, blockedVariantIds) {
    const additions = [];
    const changes = [];
    if (!legacyConfig) return { additions, changes };

    const amount = computeAmount(cart);
    const { everyOrderVariantId, conditionalVariantId, conditionalThreshold } = legacyConfig;

    if (everyOrderVariantId) {
      const giftLine = findGiftLine(cart, everyOrderVariantId);
      if (amount > 0 && !giftLine && !blockedVariantIds.includes(everyOrderVariantId)) {
        additions.push({ id: parseInt(everyOrderVariantId, 10), quantity: 1, properties: { _gift: 'true' } });
      } else if (amount <= 0 && giftLine) {
        changes.push({ id: giftLine.key, quantity: 0 });
      }
    }

    if (conditionalVariantId && conditionalThreshold) {
      const giftLine = findGiftLine(cart, conditionalVariantId);
      const hasVisitedFlag = window.PageVisitTracker?.hasVisited() || false;
      const qualifies = hasVisitedFlag && amount >= conditionalThreshold;
      if (qualifies && !giftLine && !blockedVariantIds.includes(conditionalVariantId)) {
        additions.push({
          id: parseInt(conditionalVariantId, 10),
          quantity: 1,
          properties: { _gift: 'true', _conditional: 'true' },
        });
      } else if (!qualifies && giftLine) {
        changes.push({ id: giftLine.key, quantity: 0 });
      }
    }

    return { additions, changes };
  }

  function addGifts(items) {
    return fetchWithTimeout(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    });
  }

  function applyChange(change) {
    return fetchWithTimeout(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(change),
    });
  }

  function getSectionInnerHTML(html, selector) {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  function replaceSection(rootId, selector, html) {
    if (!html) return;
    const root = document.getElementById(rootId);
    if (!root) return;
    const target = root.querySelector(selector) || root;
    target.innerHTML = getSectionInnerHTML(html, selector);
  }

  function reinitCartWidgets() {
    requestAnimationFrame(() => {
      if (typeof window.initializeOrderCount === 'function') window.initializeOrderCount();
      if (typeof window.toggleGamificationVariant === 'function') window.toggleGamificationVariant();
    });
  }

  function refreshCartUI() {
    return fetchWithTimeout(
      window.Shopify.routes.root + '?sections=cart-drawer,cart-icon-bubble,main-cart-items,main-cart-footer'
    )
      .then((response) => response.text())
      .then((responseText) => {
        const parsedState = JSON.parse(responseText);
        replaceSection('CartDrawer', '.drawer__inner', parsedState['cart-drawer']);
        replaceSection('cart-icon-bubble', '.shopify-section', parsedState['cart-icon-bubble']);
        if (document.getElementById('main-cart-items')) {
          const container = document.getElementById('MainContent')?.querySelector('.shopify-section');
          if (container && parsedState['main-cart-items']) {
            container.innerHTML = getSectionInnerHTML(parsedState['main-cart-items'], '.shopify-section');
          }
        }
        reinitCartWidgets();
        document.dispatchEvent(new CustomEvent('free-gift:synced'));
      })
      .catch((error) => console.error('[FreeGift] UI-Refresh fehlgeschlagen', error));
  }

  async function runSync() {
    const configData = readConfig();
    const legacyConfig = readLegacyConfig();
    if (!configData && !legacyConfig) return;

    let cart;
    try {
      cart = await fetchWithTimeout(window.Shopify.routes.root + 'cart.js').then((r) => r.json());
    } catch (error) {
      console.error('[FreeGift] Cart konnte nicht geladen werden', error);
      return;
    }

    const blockedVariantIds = getBlockedVariantIds();
    let additions = [];
    let changes = [];

    if (configData) {
      const rules = selectRules(configData.config, configData.country);
      const built = buildActions(cart, rules, blockedVariantIds);
      additions = additions.concat(built.additions);
      changes = changes.concat(built.changes);

      if (configData.config.enabled === false) {
        const managedVariantIds = collectAllConfiguredVariantIds(configData.config);
        changes = changes.concat(collectManagedOrphanGiftLines(cart, managedVariantIds));
      }
    }

    const legacyBuilt = buildLegacyActions(cart, legacyConfig, blockedVariantIds);
    additions = additions.concat(legacyBuilt.additions);
    changes = changes.concat(legacyBuilt.changes);

    if (!additions.length && !changes.length) return;

    try {
      if (additions.length) await addGifts(additions);
      for (const change of changes) {
        await applyChange(change);
      }
      await refreshCartUI();
    } catch (error) {
      console.error('[FreeGift] Sync fehlgeschlagen', error);
    }
  }

  async function sync() {
    if (isSyncing) {
      rerunQueued = true;
      return;
    }
    isSyncing = true;
    try {
      await runSync();
    } finally {
      isSyncing = false;
      if (rerunQueued) {
        rerunQueued = false;
        sync();
      }
    }
  }

  window.HensslersFreeGift = { sync };

  document.addEventListener('DOMContentLoaded', sync);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, sync);
  }
})();
